import { ThemeFont, ThemeMode } from '@prisma/client'
import { useFetcher } from '@remix-run/react'
import chroma from 'chroma-js'
import { TrashIcon, XIcon } from 'lucide-react'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { ShopContext } from '~/contexts'
import { detectCustomizerFieldsChange } from '~/helpers/customizer'
import { Form } from '~/helpers/form'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { patchShopSchema } from '~/schemas/shop'
import { Button, Dialog, Input, ColorPicker, SegmentedControl } from '~/ui'
import { cn } from '~/utils/classnames'

import { FONT_VALUES, MODE_VALUES } from './theme-customizer-constants'
import { useThemeCustomizerContext } from './theme-customizer-context'

interface ThemeCustomizerProps {
  className?: string
}

export const ThemeCustomizer = ({ className }: ThemeCustomizerProps) => {
  const shop = useShop()
  const [shopContext] = useContext(ShopContext)
  const { setIsOpen } = useThemeCustomizerContext()

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const action = useMemo(() => makeShopOwnPath(shop, '/settings?index'), [shop])

  const handleClose = useCallback(() => {
    const hasUnsavedChanges = detectCustomizerFieldsChange(shop, shopContext)

    if (hasUnsavedChanges) {
      setIsConfirmationModalOpen(true)
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [setIsOpen, shop, shopContext])

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <header className="text-highlight flex h-(--top-bar-height) items-center gap-2 px-3 pt-1">
        <span className="size-4" />
        <span className="text-highlight text-sm font-medium">Customizer</span>
        <button
          className="bg-foreground/6 ml-auto cursor-pointer rounded-full p-1"
          onClick={handleClose}
          aria-label="Close Theme Customizer"
        >
          <XIcon className="size-3" />
        </button>
      </header>
      <ThemeCustomizerForm action={action} />
      {isConfirmationModalOpen && (
        <ThemeCustomizerConfirmationModal
          action={action}
          onClose={() => {
            flushSync(() => {
              setIsConfirmationModalOpen(false)
              setIsOpen(false)
            })
          }}
          onDismiss={() => setIsConfirmationModalOpen(false)}
        />
      )}
    </div>
  )
}

interface ThemeCustomizerFormProps {
  action: string
}

const ThemeCustomizerForm = ({ action }: ThemeCustomizerFormProps) => {
  const { t } = useTranslation()
  const fetcher = useFetcher()
  const shop = useShop()
  const { setIsOpen } = useThemeCustomizerContext()

  const [contextShop, setContextShop] = useContext(ShopContext)
  const [error, setError] = useState<string | null>(null)

  // Store original values for reverting on error
  const originalValues = useRef({
    themeColor: shop.themeColor,
    themeMode: shop.themeMode || ThemeMode.light,
    themeFont: shop.themeFont || ThemeFont.sans,
  })

  const color = useColorInput(contextShop.themeColor)
  const [font, setFont] = useState(contextShop.themeFont || ThemeFont.sans)
  const [mode, setMode] = useState(contextShop.themeMode || ThemeMode.light)

  // Optimistic updates - update contextShop immediately when values change
  const handleColorChange = useCallback(
    (newColor: string | null) => {
      setContextShop((prev) => ({
        ...prev,
        themeColor: newColor,
      }))
    },
    [setContextShop]
  )

  const handleFontChange = useCallback(
    (newFont: ThemeFont) => {
      setFont(newFont)
      setContextShop((prev) => ({
        ...prev,
        themeFont: newFont,
      }))
    },
    [setContextShop]
  )

  const handleModeChange = useCallback(
    (newMode: ThemeMode) => {
      setMode(newMode)
      setContextShop((prev) => ({
        ...prev,
        themeMode: newMode,
      }))
    },
    [setContextShop]
  )

  // Store current values in refs to avoid dependency issues
  const currentValues = useRef({ color, mode, font })
  currentValues.current = { color, mode, font }

  // Handle API response
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (typeof fetcher.data === 'object' && 'error' in fetcher.data) {
        // API failed - revert to original values
        setContextShop(originalValues.current)
        setFont(originalValues.current.themeFont)
        setMode(originalValues.current.themeMode)

        if (originalValues.current.themeColor) {
          // Set hex first, which will update HSV automatically
          currentValues.current.color.setHex(originalValues.current.themeColor)
        } else {
          currentValues.current.color.reset()
        }
        setError(
          t('components.customizer_bar.save_error') || 'Failed to save changes'
        )
      } else {
        // API succeeded - update original values and context shop to reflect saved state
        const savedValues = {
          themeColor: currentValues.current.color.hex,
          themeMode: currentValues.current.mode,
          themeFont: currentValues.current.font,
        }
        originalValues.current = savedValues
        setContextShop(savedValues)
        setError(null)
        setIsOpen(false)
      }
    }
  }, [fetcher.state, fetcher.data, setContextShop, t, setIsOpen])

  return (
    <Form
      mode="onBlur"
      method="PATCH"
      action={action}
      fetcher={fetcher}
      schema={patchShopSchema.pick({
        themeColor: true,
        themeMode: true,
        themeFont: true,
      })}
      values={{
        themeColor: color.hex,
        themeMode: mode,
        themeFont: font,
      }}
      className="text-highlight flex h-full flex-col gap-4 overflow-auto px-3 pt-4 pl-1"
    >
      {({ Field, register, submit }) => (
        <>
          <div>
            <h2 className="text-foreground text-xs font-medium">
              {t('components.customizer_bar.base_color')}
            </h2>
            <p className="text-foreground/50 mt-0.5 text-xs">
              {t('components.customizer_bar.base_color_description')}
            </p>
          </div>

          <Field name="themeColor">
            {() => (
              <>
                <ColorPicker
                  color={color.hsv || [0, 1, 1]}
                  onColorChange={([h, s, v]) => {
                    // Update HSV directly (primary state)
                    color.setHsv([h, s, v])
                    // Handle color change with the computed hex
                    handleColorChange(chroma.hsv(h, s, v).hex())
                  }}
                  className="mb-4 space-y-4"
                >
                  <ColorPicker.Area />
                  <ColorPicker.Hue />
                </ColorPicker>

                <Input.Group>
                  <Input.Prefix>
                    <span
                      className="border-border/12 -ml-1 size-4 rounded-sm border"
                      style={{ backgroundColor: color.hex || 'transparent' }}
                    />
                  </Input.Prefix>

                  <Input
                    {...register('themeColor')}
                    className="w-full text-sm tabular-nums"
                    type="text"
                    value={color.hex ? color.hex.toLocaleUpperCase() : ''}
                    onChange={(e) => {
                      // Update hex input state
                      color.setHex(e.target.value)

                      // Only update context if the hex is valid
                      if (chroma.valid(e.target.value)) {
                        handleColorChange(e.target.value)
                      }
                    }}
                    placeholder={
                      t('components.customizer_bar.color_placeholder')!
                    }
                  />

                  {/* FIXME: Passing the asChild to the Suffix gives an children prop error */}
                  <Input.Suffix className="pointer-events-auto cursor-pointer">
                    <button
                      type="button"
                      className="cursor-pointer opacity-64"
                      onClick={() => {
                        color.reset()
                        handleColorChange(null)
                      }}
                      aria-label="Delete color"
                    >
                      <TrashIcon />
                    </button>
                  </Input.Suffix>
                </Input.Group>
              </>
            )}
          </Field>

          <div className="mt-4">
            <h2 className="text-foreground text-xs font-medium">
              {t('components.customizer_bar.mode')}
            </h2>
            <p className="text-foreground/50 mt-0.5 text-xs">
              {t('components.customizer_bar.mode_description')}
            </p>
          </div>

          <Field name="themeMode">
            {() => (
              <SegmentedControl
                name={register('themeMode').name}
                value={mode}
                onValueChange={(value) => handleModeChange(value as ThemeMode)}
              >
                {MODE_VALUES.map(({ value, label_key }) => (
                  <SegmentedControl.Item
                    key={value}
                    value={value}
                    className="flex-1"
                  >
                    {t(label_key)}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl>
            )}
          </Field>

          <div className="mt-4">
            <h2 className="text-foreground text-xs font-medium">
              {t('components.customizer_bar.font')}
            </h2>
            <p className="text-foreground/50 mt-0.5 text-xs">
              {t('components.customizer_bar.font_description')}
            </p>
          </div>

          <Field name="themeFont">
            {() => (
              <>
                <SegmentedControl
                  name={register('themeFont').name}
                  value={font}
                  onValueChange={(value) =>
                    handleFontChange(value as ThemeFont)
                  }
                >
                  {FONT_VALUES.map(({ value, label_key }) => (
                    <SegmentedControl.Item
                      key={value}
                      value={value}
                      className={'flex-1'}
                      style={{
                        fontFamily: 'var(--font-' + value + ')',
                      }}
                    >
                      {t(label_key)}
                    </SegmentedControl.Item>
                  ))}
                </SegmentedControl>
              </>
            )}
          </Field>

          <div className="sticky bottom-0 mt-4 flex flex-1 flex-col justify-end pb-3">
            <Button
              size="md"
              variant="primary"
              className={cn('w-full', error && 'animate-shake')}
              onClick={() => submit()}
              isLoading={fetcher.state !== 'idle'}
            >
              {fetcher.state === 'idle' && t('action.save')}
            </Button>
          </div>
        </>
      )}
    </Form>
  )
}

interface ThemeCustomizerConfirmationModalProps {
  action: string
  onClose: () => void
  onDismiss: () => void
}

const ThemeCustomizerConfirmationModal = ({
  action,
  onDismiss,
  onClose,
}: ThemeCustomizerConfirmationModalProps) => {
  const { t } = useTranslation()
  const fetcher = useFetcher()
  const shop = useShop()

  const [contextShop, setContextShop] = useContext(ShopContext)

  const handleSaveChanges = () => {
    fetcher.submit(contextShop, {
      method: 'PATCH',
      action,
    })
  }

  const handleExitWithoutSaving = () => {
    setContextShop(shop)
    onClose()
  }

  const isLoading = fetcher.state !== 'idle'

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (typeof fetcher.data === 'object' && 'error' in fetcher.data) {
        // API failed - don't close the modal, let user retry
        return
      } else {
        // close the modal
        onClose()
      }
    }
  }, [fetcher.data, fetcher.state, onClose])

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isLoading) onDismiss()
      }}
    >
      <Dialog.Content>
        <Dialog.Title>
          {t('components.customizer_bar_modal.title')}
        </Dialog.Title>

        <Dialog.Description asChild>
          <p className="text-foreground/64 text-xs font-medium">
            {t('components.customizer_bar_modal.description')}
          </p>
        </Dialog.Description>

        <Dialog.Actions>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            onClick={handleSaveChanges}
          >
            {t('action.save')}
          </Button>

          <Button
            type="submit"
            variant="outline"
            onClick={handleExitWithoutSaving}
          >
            {t('action.exit_without_saving')}
          </Button>
        </Dialog.Actions>
      </Dialog.Content>
    </Dialog>
  )
}

type ColorPickerValue = [number, number, number]

const useColorInput = (initialValue: string | null) => {
  // Primary state: Store HSV values to avoid precision loss
  const [hsv, setHsvState] = useState<ColorPickerValue | null>(() => {
    if (initialValue && chroma.valid(initialValue)) {
      return chroma(initialValue)
        .hsv()
        .map((num) => (isNaN(num) ? 0 : num)) as ColorPickerValue
    }
    return null
  })

  // Secondary state: Store hex input for direct text editing
  const [hexInput, setHexInput] = useState(initialValue)

  // Derived value: Compute hex from HSV when available
  const hex = useMemo(() => {
    if (hsv) {
      return chroma.hsv(...hsv).hex()
    }
    return hexInput
  }, [hsv, hexInput])

  const setHsv = (value: ColorPickerValue) => {
    setHsvState(value)
    // Update hex input to match the HSV value
    setHexInput(chroma.hsv(...value).hex())
  }

  const setHex = (value: string) => {
    setHexInput(value)
    // Only update HSV if the hex is valid
    if (value && chroma.valid(value)) {
      const newHsv = chroma(value)
        .hsv()
        .map((num) => (isNaN(num) ? 0 : num)) as ColorPickerValue
      setHsvState(newHsv)
    }
  }

  const reset = () => {
    setHsvState(null)
    setHexInput(null)
  }

  return { hsv, setHsv, hex, setHex, reset }
}
